/**
 * ARIA Login Page
 * Version: 1.0.0
 *
 * Username/password login (ARIA auth Option B — see AUTH_ENTRA_ID_SSO_PLAN.md).
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useMutation, gql } from '@apollo/client';
import { setAuthToken, API_BASE_URL } from '../config/apollo';
import { useAppDispatch, useAppSelector, setUser, clearSessionMessage } from '../store';

const LOGIN_MUTATION = gql`
  mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      token
      user {
        id
        email
        name
        role
      }
    }
  }
`;

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const samlError = searchParams.get('error') === 'saml';

  // Set by session expiry or an idle timeout (not a manual sign-out) — see
  // src/store/index.ts and src/hooks/useSessionTimeout.ts. Read once into
  // local state and cleared from Redux immediately, so it doesn't reappear
  // if the user navigates back to this page later in the same session.
  const sessionMessage = useAppSelector((state) => state.user.sessionMessage);
  const [shownSessionMessage, setShownSessionMessage] = useState<string | null>(null);

  useEffect(() => {
    if (sessionMessage) {
      setShownSessionMessage(sessionMessage);
      dispatch(clearSessionMessage());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionMessage]);

  const [login, { loading, error }] = useMutation(LOGIN_MUTATION);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const { data } = await login({ variables: { email, password } });
      if (!data?.login) return;

      setAuthToken(data.login.token);
      dispatch(
        setUser({
          id: data.login.user.id,
          email: data.login.user.email,
          name: data.login.user.name,
          roles: [data.login.user.role],
        }),
      );

      const redirectTo = (location.state as { from?: string } | null)?.from || '/';
      navigate(redirectTo, { replace: true });
    } catch {
      // surfaced via useMutation's `error` below
    }
  };

  return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <div className="card w-full max-w-sm p-8">
        <div className="flex flex-col items-center mb-6">
          <div className="w-10 h-10 bg-brand-600 rounded-lg flex items-center justify-center mb-3">
            <span className="text-white font-bold">A</span>
          </div>
          <h1 className="text-lg font-semibold text-gray-900">Sign in to ARIA</h1>
        </div>

        {samlError && (
          <p className="text-sm text-red-600 mb-4 text-center">
            Microsoft sign-in failed. Please try again, or sign in with your password below.
          </p>
        )}

        {shownSessionMessage && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4 text-center">
            {shownSessionMessage}
          </p>
        )}

        <a
          href={`${API_BASE_URL}/auth/saml/login`}
          className="btn-secondary w-full flex items-center justify-center gap-2 mb-4"
        >
          Sign in with Microsoft
        </a>

        <div className="relative mb-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-white px-2 text-gray-400">or</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              required
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div className="flex justify-end">
            <Link to="/forgot-password" className="text-sm text-brand-600 hover:underline">
              Forgot password?
            </Link>
          </div>

          {error && <p className="text-sm text-red-600">Invalid email or password.</p>}

          <button type="submit" disabled={loading} className="btn-primary w-full">
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
